import type { NpcHullTier, NpcUnit } from "./types";
import { loadUnits } from "./data/loadEncounters";
import { npcSubtypeKey } from "./ui/shipCanvasIcon";

// Opponent record (16.82, Don's direction) -- what the captain has
// actually met and what they did about it. Feeds the Trophy Room's
// bestiary: an unmet opponent shows as a locked silhouette, a met one
// shows its real roster stats, its dossier, and a personal kill count.
//
// Recorded at the two points where the real events already resolve
// (a hostile actually spawning into the scene, a hostile actually being
// destroyed), never inferred afterwards -- the same rule 16.76's career
// stats set for themselves, and for the same reason: a trophy that
// disagrees with what the captain remembers doing is worse than no
// trophy.
export interface BestiaryEntry {
  seen: number;
  killed: number;
}

export type BestiaryRecord = Record<string, BestiaryEntry>;

// Unit NAMES repeat across tiers -- there is a "Pirate Raider" at all
// four -- so the name alone is not a key. A T4 Raider is a genuinely
// different opponent from a T1 one (25x the EHP) and earns its own row.
export function bestiaryKey(unitName: string, hullTier: NpcHullTier): string {
  return `${unitName}|${hullTier}`;
}

export function entryFor(record: BestiaryRecord, key: string): BestiaryEntry {
  const e = record[key];
  return {
    seen: Math.max(0, Math.floor(e?.seen ?? 0)),
    killed: Math.max(0, Math.floor(e?.killed ?? 0))
  };
}

export function hasMet(record: BestiaryRecord, key: string): boolean {
  return entryFor(record, key).seen > 0;
}

export function recordSighting(record: BestiaryRecord, unitName: string, hullTier: NpcHullTier): void {
  const key = bestiaryKey(unitName, hullTier);
  const current = entryFor(record, key);
  record[key] = { ...current, seen: current.seen + 1 };
}

export function recordKill(record: BestiaryRecord, unitName: string, hullTier: NpcHullTier): void {
  const key = bestiaryKey(unitName, hullTier);
  const current = entryFor(record, key);
  // A kill implies a sighting even if the spawn hook somehow missed it --
  // an opponent that is dead has definitionally been met, and a bestiary
  // that shows "0 seen, 1 killed" would just look broken.
  record[key] = { seen: Math.max(1, current.seen), killed: current.killed + 1 };
}

// Loaded blobs are never trusted wholesale -- a save written by a build
// with different unit names, or a hand-edited export (16.44 makes saves a
// real portable file), must not be able to inject NaN into a counter.
export function normalizeBestiary(raw: unknown): BestiaryRecord {
  if (!raw || typeof raw !== "object") return {};
  const out: BestiaryRecord = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!value || typeof value !== "object") continue;
    const v = value as Partial<BestiaryEntry>;
    const seen = typeof v.seen === "number" && Number.isFinite(v.seen) ? Math.max(0, Math.floor(v.seen)) : 0;
    const killed = typeof v.killed === "number" && Number.isFinite(v.killed) ? Math.max(0, Math.floor(v.killed)) : 0;
    if (seen > 0 || killed > 0) out[key] = { seen, killed };
  }
  return out;
}

// ---- The roster the Trophy Room enumerates ---------------------------

export interface BestiaryRow {
  key: string;
  unit: NpcUnit;
  subtype: string; // dossier key -- Raider/Micro/Leader/Line/Logi/Duelist/Elite/Ancient
  ehp: number;
  // True for the four hidden-system guardians (16.68), which are derived
  // from the Alien Elite line rather than being roster units of their own.
  guardian: boolean;
}

// 16.68: a hidden system's guardian is an Alien Elite of the system's own
// tier at 1.5x EHP and DPS, named "<Elite name> (Ancient)". Lives here
// rather than in expeditionScreen.ts because the Trophy Room needs the
// same number to show a guardian's real stats before you ever meet one.
export const HIDDEN_SYSTEM_GUARDIAN_MULTIPLIER = 1.5;

export function guardianName(eliteName: string): string {
  return `${eliteName} (Ancient)`;
}

function guardianUnit(elite: NpcUnit): NpcUnit {
  return {
    ...elite,
    name: guardianName(elite.name),
    hullHp: elite.hullHp * HIDDEN_SYSTEM_GUARDIAN_MULTIPLIER,
    shieldHp: elite.shieldHp * HIDDEN_SYSTEM_GUARDIAN_MULTIPLIER,
    dps: elite.dps * HIDDEN_SYSTEM_GUARDIAN_MULTIPLIER
  };
}

// Every NPC in the real roster, faction-grouped then tier-ordered, so the
// Trophy Room's grid is derived from encounters.json rather than a second
// hand-maintained list that could drift from it.
const TIER_ORDER: Record<NpcHullTier, number> = { T1: 1, T2: 2, T3: 3, T4: 4 };
const FACTION_ORDER: Record<string, number> = { Pirate: 0, Faction: 1, Alien: 2 };

export function bestiaryRoster(units: NpcUnit[] = loadUnits()): BestiaryRow[] {
  // The four Ancients are real opponents the captain can meet (one per
  // hidden system, 16.68) but aren't roster entries in encounters.json,
  // so they're synthesized here rather than left out of the Trophy Room.
  const guardians = units
    .filter((u) => u.faction === "Alien" && npcSubtypeKey(u.name, u.faction) === "Elite")
    .map(guardianUnit);
  return [...units, ...guardians]
    .map((unit) => {
      const guardian = unit.name.endsWith("(Ancient)");
      return {
        key: bestiaryKey(unit.name, unit.hullTier),
        unit,
        subtype: guardian ? "Ancient" : npcSubtypeKey(unit.name, unit.faction),
        ehp: unit.hullHp + unit.shieldHp,
        guardian
      };
    })
    .sort(
      (a, b) =>
        (FACTION_ORDER[a.unit.faction] ?? 9) - (FACTION_ORDER[b.unit.faction] ?? 9) ||
        a.subtype.localeCompare(b.subtype) ||
        TIER_ORDER[a.unit.hullTier] - TIER_ORDER[b.unit.hullTier]
    );
}

export function metCount(record: BestiaryRecord, rows: BestiaryRow[]): number {
  return rows.filter((r) => hasMet(record, r.key)).length;
}

export function totalKills(record: BestiaryRecord): number {
  return Object.values(record).reduce((sum, e) => sum + Math.max(0, e.killed), 0);
}
